import React, { JSX, useEffect, useMemo } from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DownloadIcon from '@mui/icons-material/Download';
import HelpIcon from '@mui/icons-material/Help';
import SettingsIcon from "@mui/icons-material/Settings";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { Link } from "react-router-dom";
import LogoutModal from "./LogoutModal";
import { isMobile } from "react-device-detect";
import Header from "./Header";
import { useDispatch, useSelector } from "react-redux";
import { push } from "redux-first-history";
import { RootState } from "../store/index";
import { DownloadReturnTask, FetchReturnsTask, selectTask } from "../utils/tasks";
import { returnsSlice } from "../store/reducer/returns";
import { returnsApi } from "../api/ReturnsApi";
import LineLoader from "./Loaders/LineLoader/LineLoader";
import { TaxDocumentStatus } from "../enums/tax";
import { ListItemButton } from "@mui/material";
import { debounce } from "lodash";
import { isTaxReturnComplete, isTaxReturnAwaitingPayment } from "../hooks/TaxReturnState";

interface ISideBarProps {
  open: boolean;
  onMenuClick?: () => void;
}

interface MenuItem {
  name: string;
  icon?: JSX.Element;
  route?: string;
  userFlow?: boolean;
  onClick?: () => void;
}

const SideBar = ({ open, onMenuClick = () => {} }: ISideBarProps) => {
  const dispatch = useDispatch();
  const { frontendState: { returnSigned }, season } = useSelector((state: RootState) => state.state);
  const taxReturnComplete = isTaxReturnComplete();
  const taxReturnAwaitingPayment = isTaxReturnAwaitingPayment();
  const returnsState = useSelector((state: RootState) => state.returns);
  const fetchReturnsTask = selectTask(returnsState, FetchReturnsTask.id);
  const downloadReturnTask = selectTask(returnsState, DownloadReturnTask.id);
  const [show, setShow] = React.useState(false);

  const debouncedReturnsFetch = useMemo(() => {
    return debounce(() => {
      dispatch(returnsSlice.actions.fetchReturnsRequest());
    }, 10000, {leading: true, trailing: false})
  }, [dispatch]);

  useEffect(() => {
    if (!returnsState.returns && !fetchReturnsTask) {
      debouncedReturnsFetch();
    }
  }, [returnsState, fetchReturnsTask]);

  // TODO: think we can refactor out userFlow: false logic since app is using push() calls consistently
  let menuItems: MenuItem[] = [
    { name: "Let's Get Started", icon: <AccountCircleIcon />, route: "/user/welcome-back", userFlow: true },
    { name: "Account Settings", icon: <SettingsIcon />, route: "/settings", userFlow: true },
    { name: "Policies", icon: <TextSnippetIcon />, route: "/policies", userFlow: true },
    { name: "Help", icon: <HelpIcon />, route: "/help", userFlow: true },
    {
      name: "Logout",
      icon: <ExitToAppIcon />,
      onClick: () => {
        setShow(true);
      },
    },
  ];


  // Removes tax return filing entrypoint if at end stages of return
  if (returnSigned || 
      taxReturnComplete || 
      taxReturnAwaitingPayment || 
      !(season?.filingIsOpen ?? false)) {
    menuItems = menuItems.filter((item: any) => {
      return item.name !== "Let's Get Started";
    });
  }

  // Add Dev shortcuts when in dev environment
  if (import.meta.env.VITE_APP_ENV === 'dev') {
    let devShortcuts = [
      { name: "DEV - Balance Summary", icon: <SettingsIcon />, route: "/balance", userFlow: true },
      { name: "DEV - Review Balance", icon: <SettingsIcon />, route: "/balance/review", userFlow: true },
      { name: "DEV - Sign Return", icon: <SettingsIcon />, route: "/balance/sign", userFlow: true },
      { name: "DEV - Post Signature Transition", icon: <SettingsIcon />, route: "/transition/post-signature", userFlow: true },
    ];
    devShortcuts.forEach((link) => {
      menuItems.push(link);
    })
  }

  if (returnsState.returns) {
    const filteredReturns = returnsState.returns.filter((returnInfo) => {
      return returnInfo.state === TaxDocumentStatus.Complete;
    });
    if (filteredReturns.length) menuItems.push({
      name: "Previous Filings"
    });
    for (let i = 0; i < filteredReturns.length; i++) {
      const returnInfo = filteredReturns[i];
      menuItems.push({
        name: `${returnInfo.tax_year} Return`,
        icon: <DownloadIcon />,
        onClick: async () => {
          dispatch(returnsSlice.actions.addTask(DownloadReturnTask));
          try {
            const pdf = await returnsApi.downloadTaxReturn(returnInfo.id);
            // create file link in browser's memory
            const href = URL.createObjectURL(pdf);

            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', `${returnInfo.tax_year}-return-summary.pdf`);
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          } finally {
            dispatch(returnsSlice.actions.removeTask(DownloadReturnTask.id));
          }
        },
      });
    }
  }

  return (
    <>
    {downloadReturnTask && <LineLoader />}
    <nav className='sidebar'>
      
      {isMobile && <Header onMenuClick={onMenuClick} />}
      <Drawer
        classes={{
          paper: 'sidebar-paper',
          docked: 'docked',
        }}
        anchor="left"
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
      >
        <List className='list'>
          {/* This section is wonky because a link can be part of the user flow and should be captured in redux history,
              or its a normal link, or its a log out link
            // TODO: think we can refactor out userFlow: false logic since app is using push() calls consistently
          */}
          {menuItems.map(({ name, icon, route, userFlow, ...rest }: any, index) => {
            if (userFlow && route) {
              return (
                <ListItem key={index} disablePadding>
                  <ListItemButton {...rest} onClick={() => dispatch(push(route))} id={`sidebar-link-${index}`}>
                  <ListItemIcon className='list-item-icon'>
                    {icon}
                  </ListItemIcon>
                  <ListItemText className='list-item'>
                    <span className='list-item'> {name}</span>
                  </ListItemText>
                </ListItemButton>
                </ListItem>
              );
            } else if (route) {
              return (
                <ListItem key={index} disablePadding>
                  <Link className='list-item' to={route}>
                  <ListItemButton {...rest} id={`sidebar-link-${index}`}>
                    <ListItemIcon className='list-item-icon'>
                      {icon}
                    </ListItemIcon>
                    <ListItemText className='list-item'>
                      <span className='list-item'> {name}</span>
                    </ListItemText>
                  </ListItemButton>
                  </Link>
                </ListItem>
              );
            } else if (!icon) {
              return (
                <span className='list-section-title' key={'header_' + name}>{name}</span>);
            } else {
              return (
                <ListItem key={index} disablePadding>
                <ListItemButton {...rest} id={`sidebar-link-${index}`}>
                  <ListItemIcon className='list-item-icon'>
                    {icon}
                  </ListItemIcon>
                  <ListItemText className='list-item'>
                    <span className='list-item'> {name}</span>
                  </ListItemText>
                </ListItemButton>
                </ListItem>
              );
            }
          })
          }
          {isMobile && (
            <>
            <hr></hr>
            <ListItem>
              <button
                onClick={onMenuClick}
                className={`list-item close-button`}
              >
                Close Menu
              </button>
            </ListItem>
            </>
          )}
        </List>
      </Drawer>
      <LogoutModal show={show} setShow={setShow} />
    </nav>
    </>
  );
};

export default SideBar;
